import type { ReactNode} from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { CountdownRendererFn, CountdownRenderProps } from 'react-countdown';
import ReactCountdown from 'react-countdown';

import styles from './Countdown.module.scss';

type CountdownDisplayFormat = 'REMAINING_DAYS' | 'TIMER' | 'AUTO';

type CountdownTimerFormat = 'COMPACT' | 'SPARSE';

type CountdownTimerPrecision = 'SECONDS' | 'MINUTES';

type CountdownTemplate = string | ((value: string) => string);

type CountdownStatus = 'STARTED' | 'PAUSED' | 'STOPPED' | 'COMPLETED';

interface CountdownProps {
  date: Date;
  displayFormat?: CountdownDisplayFormat;
  timerFormat?: CountdownTimerFormat;
  timerPrecision?: CountdownTimerPrecision;
  templates?: Partial<Record<CountdownStatus, CountdownTemplate>>;
  valueWhenComplete?: ReactNode;
  onComplete?: () => void;
}

type RendererFactoryProps = Required<
  Omit<CountdownProps, 'date' | 'displayFormat' | 'onComplete' | 'valueWhenComplete' | 'templates'>
> & {
  template: CountdownTemplate;
};

const createRemainingDaysRenderer =
  ({ template }: RendererFactoryProps) =>
  (props: CountdownRenderProps) => {
    return typeof template === 'function'
      ? template(`${props.days} ${props.days === 1 ? 'day' : 'days'}`)
      : template;
  };

interface CountdownTimerFormatElement {
  value: number;
  unit: 'days' | 'hours' | 'minutes' | 'seconds';
}

const DEFAULT_TEMPLATE = (value: string) => value;

const TIMER_RENDERERS: Record<
  CountdownTimerFormat,
  (elements: Array<CountdownTimerFormatElement>) => string
> = {
  COMPACT: (elements) => {
    return elements
      .filter(({ value, unit }) => !(unit === 'hours' && value === 0))
      .map(({ value }) => value.toString().padStart(2, '0'))
      .join(':');
  },
  SPARSE: (elements) => {
    return elements.map(({ value, unit }) => `${value}${unit[0]}`).join(' ');
  },
};

const createTimerRenderer = ({ timerPrecision, timerFormat, template }: RendererFactoryProps) => {
  return ({ days, hours, minutes, seconds }: CountdownRenderProps) => {
    const elements: Array<CountdownTimerFormatElement> = [
      {
        value: days,
        unit: 'days',
      },
      {
        value: hours,
        unit: 'hours',
      },
      {
        value: minutes,
        unit: 'minutes',
      },
      {
        value: seconds,
        unit: 'seconds',
      },
    ];

    if (timerPrecision === 'MINUTES') {
      elements.pop();
    }

    if (days <= 0) {
      elements.splice(0, 1);
    }

    return typeof template === 'function'
      ? template(TIMER_RENDERERS[timerFormat](elements))
      : template;
  };
};

const createAutoRenderer =
  ({ timerFormat, timerPrecision }: RendererFactoryProps) =>
  (props: CountdownRenderProps) => {
    const renderRemainingDays = createRemainingDaysRenderer({
      timerFormat,
      timerPrecision,
      template: DEFAULT_TEMPLATE,
    });
    const renderTimer = createTimerRenderer({
      timerFormat,
      timerPrecision,
      template: DEFAULT_TEMPLATE,
    });

    return props.days >= 1 ? renderRemainingDays(props) : renderTimer(props);
  };

const RENDERER_FACTORIES: Record<
  CountdownDisplayFormat,
  (props: RendererFactoryProps) => CountdownRendererFn
> = {
  REMAINING_DAYS: createRemainingDaysRenderer,
  TIMER: createTimerRenderer,
  AUTO: createAutoRenderer,
};

const Countdown = ({
  date,
  displayFormat = 'AUTO',
  timerPrecision = 'SECONDS',
  timerFormat = 'COMPACT',
  templates = {},
  onComplete,
}: CountdownProps) => {
  const [status, setStatus] = useState<CountdownStatus>();

  const render = useMemo(() => {
    return RENDERER_FACTORIES[displayFormat]({
      timerPrecision,
      timerFormat,
      template: (status && templates[status]) ?? DEFAULT_TEMPLATE,
    });
  }, [displayFormat, timerPrecision, timerFormat, templates, status]);

  const handleStarted = useCallback(() => {
    setStatus('STARTED');
  }, []);

  const handlePaused = useCallback(() => {
    setStatus('PAUSED');
  }, []);

  const handleStopped = useCallback(() => {
    setStatus('STOPPED');
  }, []);

  const handleCompleted = useCallback(() => {
    setStatus('COMPLETED');
    onComplete?.();
  }, [onComplete]);

  return (
    <span className={styles.self}>
      <ReactCountdown
        date={date}
        renderer={render}
        onStart={handleStarted}
        onPause={handlePaused}
        onStop={handleStopped}
        onComplete={handleCompleted}
        key={date.toISOString()}
      />
    </span>
  );
};

export { Countdown, type CountdownProps, type CountdownDisplayFormat };
